import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { selectTheme } from "../../../Store/features/design/designSlice";
import { getCampaignById, updateCampaign } from "../../../Service/user";
import "../CreateCampaign/CreateCampaign.scss";

const EditCampaign = () => {
  const { campaignId } = useParams();
  const theme = useSelector(selectTheme);
  const navigate = useNavigate();
  
  const [campaignData, setCampaignData] = useState({
    campaignName: "",
    emailSubject: "",
    emailBody: "",
  });
  
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchCampaignDetails = async () => {
      try {
        setLoading(true);
        const response = await getCampaignById(campaignId);
        if (response.status === 200) {
          const campaign = response.campaign;
          setCampaignData({
            campaignName: campaign.campaignName,
            emailSubject: campaign.template?.emailSubject || "",
            emailBody: campaign.template?.emailBody || "",
          });
        } else {
          toast.error(response.message || "Failed to fetch campaign details");
          navigate("/campaign");
        }
      } catch (error) {
        console.error("Error fetching campaign details:", error);
        toast.error("An error occurred while fetching campaign details");
        navigate("/campaign");
      } finally {
        setLoading(false);
      }
    };
    
    fetchCampaignDetails();
  }, [campaignId, navigate]);
  
  const handleGoBack = () => {
    navigate("/campaign");
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCampaignData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleEditorChange = (content) => {
    setCampaignData(prev => ({
      ...prev,
      emailBody: content
    }));
  };
  
  const handleSaveChanges = async () => {
    if (!campaignData.campaignName) {
      toast.error("Please enter a campaign name");
      return;
    }
    
    try {
      const response = await updateCampaign(campaignId, {
        campaignName: campaignData.campaignName,
        template: {
          emailSubject: campaignData.emailSubject,
          emailBody: campaignData.emailBody
        }
      });
      
      if (response.status === 200) {
        toast.success("Campaign updated successfully");
        navigate(`/campaign/detail/${campaignId}`);
      } else {
        toast.error(response.message || "Failed to update campaign");
      }
    } catch (error) {
      console.error("Error updating campaign:", error);
      toast.error("An error occurred while updating the campaign");
    }
  };
  
  if (loading) {
    return (
      <div className={`create-campaign ${theme}`}>
        <div className="loading-state">Loading campaign details...</div>
      </div>
    );
  }
  
  return (
    <div className={`create-campaign ${theme}`}>
      <div className="campaign-header">
        <button className="back-button" onClick={handleGoBack}>
          <FaArrowLeft className="back-icon" /> Back to Campaigns
        </button>
      </div>

      <div className="campaign-content">
        <h2>Edit Campaign</h2>
        
        <div className="form-section">
          <label className="form-label" htmlFor="campaignName">Campaign Name</label>
          <input
            type="text"
            id="campaignName"
            name="campaignName"
            className="form-input"
            placeholder="Enter campaign name"
            value={campaignData.campaignName}
            onChange={handleInputChange}
          />
        </div>

        <div className="compose-section">
          <div className="compose-header">
            <h3 className="compose-title">Edit Email Content</h3>
          </div>

          <div className="subject-section">
            <label className="form-label" htmlFor="emailSubject">Subject</label>
            <input
              type="text"
              id="emailSubject"
              name="emailSubject"
              className="form-input"
              placeholder="Enter email subject"
              value={campaignData.emailSubject}
              onChange={handleInputChange}
            />
          </div>

          <div className="content-section">
            <label className="form-label">Email Content</label>
            <div className="quill-container">
              <ReactQuill
                theme="snow"
                value={campaignData.emailBody}
                onChange={handleEditorChange}
                placeholder="Compose your email..."
              />
            </div>
          </div>
        </div>
      </div>

      <div className="campaign-footer">
        <button className="footer-button secondary" onClick={handleGoBack}>
          Cancel
        </button>
        <button className="footer-button primary" onClick={handleSaveChanges}>
          Save Changes
        </button>
      </div>
    </div>
  );
};

export default EditCampaign; 